import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import StatsBar from "../components/StatsBar";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FolderOpen, Plus, Search, Trash2, Edit, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  
  // Separate state for create and edit forms to prevent focus issues
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchCollections = async () => {
    try {
      const params = search ? { search } : {};
      const { data } = await axios.get(`${API}/collections`, { 
        params, 
        withCredentials: true 
      });
      setCollections(data);
    } catch (err) {
      toast.error("Failed to fetch collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/collections`, { name: createName, description: createDesc }, { withCredentials: true });
      toast.success("Collection created");
      setCreateOpen(false);
      setCreateName("");
      setCreateDesc("");
      fetchCollections();
    } catch (err) {
      toast.error("Failed to create collection");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editName.trim() || !editingCollection) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/collections/${editingCollection.id}`, { name: editName, description: editDesc }, { withCredentials: true });
      toast.success("Collection updated");
      setEditOpen(false);
      setEditingCollection(null);
      setEditName("");
      setEditDesc("");
      fetchCollections();
    } catch (err) {
      toast.error("Failed to update collection");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this collection and all its images?")) return;
    
    try {
      await axios.delete(`${API}/collections/${id}`, { withCredentials: true });
      toast.success("Collection deleted");
      fetchCollections();
    } catch (err) {
      toast.error("Failed to delete collection");
    }
  };

  const openEdit = (collection) => {
    setEditingCollection(collection);
    setEditName(collection.name);
    setEditDesc(collection.description || "");
    setEditOpen(true);
  };

  const handleCreateOpenChange = (open) => {
    setCreateOpen(open);
    if (!open) {
      setCreateName("");
      setCreateDesc("");
    }
  };

  const handleEditOpenChange = (open) => {
    setEditOpen(open);
    if (!open) {
      setEditingCollection(null);
      setEditName("");
      setEditDesc("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-0" data-testid="collections-page">
      <StatsBar />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Collections
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Organize your t-shirt designs into projects
          </p>
        </div>
        
        <Dialog open={createOpen} onOpenChange={handleCreateOpenChange}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="create-collection-btn">
              <Plus size={16} className="mr-2" />
              New Collection
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-black">Create Collection</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Collection name"
                  className="rounded-none border-gray-300 bg-white text-black"
                  required
                  autoFocus
                  data-testid="create-collection-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
                <Textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="Optional description"
                  className="rounded-none border-gray-300 bg-white text-black resize-none"
                  rows={3}
                  data-testid="create-collection-desc"
                />
              </div>
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full rounded-none bg-black text-white hover:bg-gray-800"
                data-testid="create-collection-submit"
              >
                {submitting ? "Saving..." : "Create Collection"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search collections..."
            className="pl-10 rounded-none border-gray-300"
            data-testid="search-collections"
          />
        </div>
      </div>

      {/* Collections Grid */}
      {collections.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-8 text-center">
          <FolderOpen className="mx-auto text-gray-400 mb-3" size={32} />
          <p className="text-gray-500 text-sm">
            {search ? "No collections found" : "No collections yet"}
          </p>
          {!search && (
            <Button
              onClick={() => setCreateOpen(true)}
              variant="outline"
              className="mt-4 rounded-none"
            >
              Create your first collection
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((collection) => (
            <div 
              key={collection.id}
              className="border border-gray-200 hover:border-black transition-colors group"
              data-testid={`collection-${collection.id}`}
            >
              <Link to={`/collections/${collection.id}`} className="block p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-black text-white flex items-center justify-center">
                    <FolderOpen size={18} />
                  </div>
                  <ArrowRight className="text-gray-400 group-hover:text-black transition-colors" size={18} />
                </div>
                <h3 className="font-heading font-bold text-lg mb-1 truncate">{collection.name}</h3>
                <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                  {collection.description || "No description"}
                </p>
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  {collection.image_count} {collection.image_count === 1 ? "image" : "images"}
                </p>
              </Link>
              
              <div className="flex border-t border-gray-200">
                <button
                  onClick={() => openEdit(collection)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-200"
                  data-testid={`edit-collection-${collection.id}`}
                >
                  <Edit size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(collection.id)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  data-testid={`delete-collection-${collection.id}`}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={handleEditOpenChange}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Edit Collection</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Collection name"
                className="rounded-none border-gray-300 bg-white text-black"
                required
                autoFocus
                data-testid="edit-collection-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Description</Label>
              <Textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                placeholder="Optional description"
                className="rounded-none border-gray-300 bg-white text-black resize-none"
                rows={3}
                data-testid="edit-collection-desc"
              />
            </div>
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full rounded-none bg-black text-white hover:bg-gray-800"
              data-testid="edit-collection-submit"
            >
              {submitting ? "Saving..." : "Update Collection"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
